/* eslint-disable max-len */
import React, {useState, useEffect, useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import moment from 'moment';

import {contentUrlSolver, isOffscreen} from './../../helpers';
import * as FavouriteActions from '../../store/actions/favourite';
import * as ToastActions from '../../store/actions/toast';

import {ReactComponent as KeyIcon} from './../../assets/key_icon.svg';
import {ReactComponent as JoystickIconDark} from './../../assets/games_icon.svg';
import {
    SubTitle,
    Background,
    Container,
    Duration,
    FavouriteButton,
    FavouriteIcon,
    Footer,
    HeartIconContainer,
    Highlight,
    Joystick,
    LiveCircle,
    OverlayClick,
    PlayButton,
    ShowCharacter,
    ShowLogo,
    Title,
    GameIconContainer,
    DurationContainer,
} from './ContentBox.styles';

const ContentBox = ({
    data,
    background,
    title,
    sub,
    highlight,
    duration,
    action,
    type,
    disabled,
    hideFavouriteButton,
    hidePlayButton,
    featured,
    live,
    cnito,
    className,
    ...props
}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const containerRef = React.useRef();

    const {shows, episodes} = useSelector(state => state.favourite);
    const config = useSelector(state => state.config);
    const user = useSelector(state => state.user);
    const idp = user && user.idp;

    const [animateIcon, setAnimateIcon] = useState(false);
    const [boxIsOffScreen, setBoxIsOffScreen] = useState(false);

    const is_unlocked =
        // need to be fixed, see how is
        // the new way of check if content is unlocked
        true ||
        // (user && user.jwtToken) ||
        type === 'game' ||
        (data.platformVariants &&
            data.platformVariants[0] &&
            data.platformVariants[0].technicalPackages &&
            data.platformVariants[0].technicalPackages.find(
                tech_package =>
                    tech_package.packageName.toLowerCase() === 'free',
            ) !== undefined);

    const isEpisode =
        data.duration >= 0 && data.season >= 0 && data.episode >= 0;

    const favourite = isEpisode
        ? episodes
            ? episodes.find(item => item.id.toString() === data.id.toString())
            : false
        : shows
        ? shows.find(item => item.id.toString() === data.id.toString())
        : false;

    const handleClick = e => {
        if (!is_unlocked && isEpisode) {
            dispatch({
                type: 'MODAL_SHOW',
                modal: {
                    type: 'unlock',
                },
            });

            return;
        }

        if (action) {
            action();
        }
    };

    const handleToggleFavorite = e => {
        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                pageName: `cnplus.com${history.location.pathname}`,
                interaction: `cnplus|${
                    favourite ? 'remove from' : 'add to'
                } favorites`,
                domain: 'cnplus.com',
                brand: 'cartoon network',
                franchise: title,
                interactionevent: '1',
                platform: 'web',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                avsid: data.id,
                houseid: data.externalId,
                showname: title + (isEpisode ? ` ${sub}` : ''),
                favoritetype: isEpisode ? 'episode' : 'show',
            },
        });

        setAnimateIcon(!favourite);

        if (isEpisode) {
            !favourite
                ? dispatch(FavouriteActions.addEpisodeFavourite(data))
                : dispatch(FavouriteActions.removeEpisodeFavourite(data));
        } else {
            !favourite
                ? dispatch(FavouriteActions.addShowFavourite(data))
                : dispatch(FavouriteActions.removeShowFavourite(data));
        }

        setTimeout(
            () => {
                dispatch(
                    ToastActions.show(
                        <>
                            <FavouriteIcon
                                src={require('../../assets/favourites_icon.png')}
                                active={!favourite ? 'true' : null}
                            />
                            {t(
                                `${isEpisode ? 'video' : 'shows'}.${
                                    !favourite ? 'favorited' : 'unfavorited'
                                }`,
                            )}
                        </>,
                    ),
                );
            },
            !favourite ? 600 : 0,
        );
    };

    const memoizedShowIcons = useMemo(() => {
        return (
            <>
                <ShowLogo
                    cnito={cnito}
                    animated={!boxIsOffScreen}
                    loading="lazy"
                    src={contentUrlSolver(
                        config,
                        data.image,
                        'SHOW-LOGO',
                        'png',
                    )}
                />
                <ShowCharacter
                    cnito={cnito}
                    animated={!boxIsOffScreen}
                    loading="lazy"
                    src={contentUrlSolver(
                        config,
                        data.image,
                        'CHARECTER-MAIN',
                        'png',
                    )}
                />
            </>
        );

        // eslint-disable-next-line
    }, [data.image, boxIsOffScreen]);

    useEffect(() => {
        handleWindowScroll();

        window.addEventListener('scroll', handleWindowScroll);

        return () => {
            window.removeEventListener('scroll', handleWindowScroll);
        };
        // eslint-disable-next-line
    }, []);

    const handleWindowScroll = () => {
        if (containerRef.current) {
            let tempValue = isOffscreen(containerRef.current);
            setBoxIsOffScreen(tempValue);
            if (!tempValue) {
                window.removeEventListener('scroll', handleWindowScroll);
            }
        }
    };

    return !props.favouritesGrid || (props.favouritesGrid && favourite) ? (
        <Container
            title={title + (data.objectType !== 'VIDEO' ? '' : ' ' + sub)}
            isEpisode={isEpisode}
            type={type}
            ref={containerRef}
            cnito={cnito}
            className={className}
            {...props}
        >
            <OverlayClick onClick={handleClick} />

            <Background
                isEpisode={isEpisode}
                type={type}
                featured={featured}
                background={background}
                horizontal={props.horizontal}
                cnito={cnito}
            >
                {data.image && !isEpisode && type !== 'game' && (
                    <>{memoizedShowIcons}</>
                )}
            </Background>

            {highlight && <Highlight>{highlight}</Highlight>}

            {(duration || live) && !cnito && (
                <Duration live={!!live} horizontal={props.horizontal}>
                    {live ? (
                        <>
                            <LiveCircle title="" aria-hidden="true" />
                            {t('liveCarousel.live')}
                        </>
                    ) : (
                        duration
                    )}
                </Duration>
            )}

            {!props.horizontal && !hideFavouriteButton && user.jwtToken && (
                <FavouriteButton
                    animated={!boxIsOffScreen}
                    active={favourite}
                    onClick={handleToggleFavorite}
                    isEpisode={isEpisode}
                >
                    <HeartIconContainer
                        animated={animateIcon ? 'true' : 'false'}
                    >
                        <img
                            alt="Heart Icon"
                            src={require('../../assets/favourites_icon.png')}
                            width="100%"
                        />
                    </HeartIconContainer>
                </FavouriteButton>
            )}

            {(isEpisode || (type === 'game' && !featured)) &&
                !props.horizontal &&
                !hidePlayButton && (
                    <PlayButton unlocked={is_unlocked} type={type}>
                        {is_unlocked ? (
                            <>
                                {type === 'game' ? (
                                    <Joystick />
                                ) : (
                                    <img
                                        src={require('../../assets/play_icon_white.png')}
                                        alt="Play"
                                    />
                                )}
                            </>
                        ) : (
                            <KeyIcon />
                        )}
                    </PlayButton>
                )}

            {(title || sub) &&
                (isEpisode || (type === 'game' && !featured)) &&
                !cnito && (
                    <Footer
                        horizontal={props.horizontal}
                        cnito={cnito}
                        type={type}
                    >
                        <Title horizontal={props.horizontal} cnito={cnito}>
                            {sub}
                        </Title>
                        <SubTitle horizontal={props.horizontal} cnito={cnito}>
                            {title}
                        </SubTitle>
                    </Footer>
                )}

            {props.children}

            {cnito && !props.horizontal && type === 'game' && (
                <GameIconContainer>
                    <JoystickIconDark />
                </GameIconContainer>
            )}

            {cnito && !props.horizontal && isEpisode && (
                <DurationContainer>
                    <img
                        src={require('../../assets/play_icon.png')}
                        alt="Play"
                    />{' '}
                    {data.duration &&
                        moment()
                            .startOf('day')
                            .seconds(data.duration)
                            .format('mm:ss')}
                </DurationContainer>
            )}
        </Container>
    ) : null;
};

export default ContentBox;
